import {
  Interview_Reply_Event,
  InterviewCurrentMessagesQuery,
  useInterviewSendAnswerMutation,
} from '@generated/graphql';
import { Stack } from '@mui/material';
import { CircularLoading, useToast } from '@myhrbox/myhrbox-frontend-core';
import Card from 'components/Card';
import TextFormatter from 'components/TextFormatter';
import { useUnit } from 'effector-react';
import usePreventCopy from 'hooks/usePreventCopy';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { $insertCount, $insertSymbols } from 'store/assessment/model';
import {
  $questionTimer,
  startQuestionTimer,
  stopQuestionTimer,
} from 'store/assessment/questionTimer';
import { ChatMessage, OnInputSubmit } from '../types';
import Input from './Input';
import { AIMessageSx, userMessageSx } from './constants';

interface ChatProps {
  sessionId: string;
  messages: InterviewCurrentMessagesQuery['interviewCurrentMessages'] | null | undefined;
  loading: boolean;
  setMessages: Dispatch<SetStateAction<ChatMessage[] | null>>;
  setInterviewReplyLoading: Dispatch<SetStateAction<boolean>>;
}

const Chat = ({
  sessionId,
  messages,
  loading: fetchLoading,
  setMessages,
  setInterviewReplyLoading,
}: ChatProps) => {
  const { t } = useTranslation();
  const { showErrorToast } = useToast();
  const cardRef = useRef<HTMLDivElement>(null);
  const [previousQuestionTimer, setPreviousQuestionTimer] = useState(0);

  const {
    handlePreventSelectStart,
    handlePreventCopy,
    handlePreventContextMenu,
    preventCopyStyles,
  } = usePreventCopy();

  const [questionTimer, insertCount, insertSymbols] = useUnit([
    $questionTimer,
    $insertCount,
    $insertSymbols,
  ]);

  const [interviewSendAnswer, { loading: interviewSendAnswerLoading }] =
    useInterviewSendAnswerMutation();

  const onInputSubmit: OnInputSubmit = async (formData) => {
    const { message } = formData;

    try {
      const res = await interviewSendAnswer({
        variables: {
          input: {
            answer: message,
            sessionId,
            time: questionTimer - previousQuestionTimer,
            metadata: {
              insertCount,
              insertSymbols,
            },
          },
        },
      });
      if (res.data?.interviewSendAnswer) {
        setInterviewReplyLoading(true);
        const messageObject: ChatMessage = {
          content: message,
          event: Interview_Reply_Event.CandidateMessage,
        };

        setMessages((prev) => (prev ? [...prev, messageObject] : [messageObject]));
      }

      return true;
    } catch {
      showErrorToast(t('errors.common'));

      return false;
    }
  };

  useEffect(() => {
    if (interviewSendAnswerLoading) {
      stopQuestionTimer();
      setPreviousQuestionTimer(questionTimer);
    } else {
      startQuestionTimer();
    }
  }, [interviewSendAnswerLoading]);

  const loading = fetchLoading || interviewSendAnswerLoading;

  return (
    <Card
      ref={cardRef}
      sx={{
        maxWidth: '100%',
        px: 2,
        py: 3,
        borderColor: 'base.100',
        overflowY: 'auto',
        maxHeight: `calc(100vh - ${cardRef?.current?.getBoundingClientRect()?.top || 0}px - 80px)`,
      }}>
      <Stack spacing={3}>
        {messages?.map((message, index) => {
          const isUserMessage = message.event === Interview_Reply_Event.CandidateMessage;

          return (
            <Stack
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              sx={{
                width: 'fit-content',
                boxShadow: '0px 0px 7.3px 0px rgba(164, 184, 253, 0.24)',
                px: 2,
                py: 1,
                borderRadius: 4,
                ...(isUserMessage ? userMessageSx : AIMessageSx),
              }}>
              <TextFormatter
                text={message.content}
                variant='subtitle18rg'
                sx={{ ...(isUserMessage ? { '&>p': { m: 0 } } : {}), ...preventCopyStyles }}
                attributesToClear={['class', 'width', 'height']}
                onSelectCapture={handlePreventSelectStart}
                onCopy={handlePreventCopy}
                onContextMenu={handlePreventContextMenu}
              />
            </Stack>
          );
        })}
        {loading && <CircularLoading size={20} sx={{ padding: 0, justifyContent: 'flex-start' }} />}
        <Input onSubmit={onInputSubmit} disabled={loading} />
      </Stack>
    </Card>
  );
};

export default Chat;
